import { SlashCircle01, XClose } from "@untitledui/icons"
import { useState } from "react"
import { Text as AriaText } from "react-aria-components"
import type { FieldError } from "react-hook-form"

import { Button } from "@/components/ui/button"
import { HintText } from "@/components/ui/input"

import useContactStore from "../../../stores/useContactStore"
import type { ContactRoleFields, SchoolContactListItem } from "../../../types"
import SchoolContact from "../school_contact/school_contact"

interface SchoolTeamProps {
  accountId: number
  onChange?: (contactIds: string[]) => void
  value?: string[]
  error?: FieldError
  isDisabled?: boolean
}

const ROLE_LABELS: Record<string, string> = {
  principal: "Principal",
  wellness_coordinator: "Wellness Coordinator",
  supervising_teacher: "Supervising Teacher",
  administrative: "Administrative",
  after_hours: "After-hours",
  other: "Other",
}

const SchoolTeam: React.FC<SchoolTeamProps> = ({
  accountId,
  onChange,
  value,
  error,
  isDisabled,
}) => {
  const [addMode, setAddMode] = useState(false)
  const allContacts = useContactStore((s) => s.contacts)
  const getContactById = useContactStore((s) => s.getById)

  const team = (value ?? [])
    .map((id) => getContactById(id))
    .filter((c): c is SchoolContactListItem => c !== undefined)

  const noDuplicateContacts = allContacts.filter(
    (contact) => !team.some((t) => t.id === contact.id),
  )

  const formatRoles = (roles: ContactRoleFields[]): string => {
    if (!roles || roles.length === 0) return ""
    return roles
      .map((r) =>
        r.role_type === "other"
          ? r.detail
          : (ROLE_LABELS[r.role_type] ?? r.role_type),
      )
      .join(", ")
  }

  const handleAddToTeam = (contact: SchoolContactListItem) => {
    onChange?.([...(value ?? []), String(contact.id)])
    setAddMode(false)
  }

  const handleRemoveFromTeam = (contact: SchoolContactListItem) => {
    onChange?.((value ?? []).filter((id) => String(id) !== String(contact.id)))
  }

  return (
    <div className="flex flex-col items-stretch gap-4">
      <div className="flex flex-col gap-2">
        {team.map((contact) => (
          <div key={contact.id} className="flex">
            <div className="flex w-full min-w-0 flex-1 flex-col">
              <div className="flex flex-wrap gap-x-2">
                <AriaText
                  slot="label"
                  className="text-md text-primary truncate font-medium whitespace-nowrap"
                >
                  {`${contact.first_name} ${contact.last_name} Test`}
                </AriaText>
                <AriaText
                  slot="description"
                  className="text-md text-tertiary whitespace-nowrap"
                >
                  {formatRoles(contact.contact_roles) || ""}
                </AriaText>
              </div>
              <p className="text-quaternary truncate text-sm">
                {[contact.email, contact.telephone_number]
                  .filter(Boolean)
                  .join(" · ") || ""}
              </p>
            </div>
            <div className="pl-4">
              <Button
                color="secondary"
                size="sm"
                iconLeading={<XClose data-icon />}
                onPress={() => handleRemoveFromTeam(contact)}
                isDisabled={isDisabled}
              />
            </div>
          </div>
        ))}
      </div>
      {error && <HintText isInvalid>{error.message}</HintText>}
      {addMode && (
        <div className="flex w-full gap-2">
          <div className="flex-1">
            <SchoolContact
              accountId={accountId}
              onSelect={handleAddToTeam}
              defaultOpen={true}
              customData={noDuplicateContacts}
              aria-label="Select a team member"
            />
          </div>

          <Button
            color="secondary"
            size="sm"
            iconLeading={<SlashCircle01 data-icon />}
            onPress={() => setAddMode(false)}
          />
        </div>
      )}
      {!addMode && (
        <Button
          className="self-center"
          color="secondary"
          size="sm"
          onPress={() => setAddMode(true)}
          isDisabled={isDisabled}
        >
          Add
        </Button>
      )}
    </div>
  )
}

export default SchoolTeam
